import React from 'react';
import { format as dateFnsFormat } from 'date-fns';
import { TZDate } from '@date-fns/tz';

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const appTimezones = [
  {
    label: '(GMT-0:00) Coordinated Universal Time (UTC)',
    code: 'UTC',
    tzOffset: 0,
    iana: 'Etc/UTC',
  },
  {
    label: '(GMT-3:30) Newfoundland Standard Time (NST)',
    code: 'NST',
    tzOffset: -3.5,
    iana: 'America/St_Johns',
  },
  {
    label: '(GMT-4:00) Atlantic Standard Time (AST)',
    code: 'AST',
    tzOffset: -4,
    iana: 'America/Halifax',
  },
  {
    label: '(GMT-5:00) Eastern Standard Time (EST)',
    code: 'EST',
    tzOffset: -5,
    iana: 'America/New_York',
  },
  {
    label: '(GMT-6:00) Central Standard Time (CST)',
    code: 'CST',
    tzOffset: -6,
    iana: 'America/Chicago',
  },
  {
    label: '(GMT-7:00) Mountain Standard Time (MST)',
    code: 'MST',
    tzOffset: -7,
    iana: 'America/Denver',
  },
  {
    label: '(GMT-8:00) Pacific Standard Time (PST)',
    code: 'PST',
    tzOffset: -8,
    iana: 'America/Los_Angeles',
  },
  {
    label: '(GMT-9:00) Alaska Standard Time (AKST)',
    code: 'AKST',
    tzOffset: -9,
    iana: 'America/Anchorage',
  },
  {
    label: '(GMT-10:00) Hawaii Standard Time (HST)',
    code: 'HST',
    tzOffset: -10,
    iana: 'Pacific/Honolulu',
  },
];

const tzAbbreviationToIana = {
  UTC: 'Etc/UTC',
  GMT: 'Etc/GMT',
  NST: 'America/St_Johns', // Newfoundland Standard Time (UTC-3:30)
  AST: 'America/Halifax', // Atlantic Standard Time (UTC-4)
  EST: 'America/New_York', // Eastern Standard Time (UTC-5)
  CST: 'America/Chicago', // Central Standard Time (UTC-6)
  MST: 'America/Denver', // Mountain Standard Time (UTC-7)
  PST: 'America/Los_Angeles', // Pacific Standard Time (UTC-8)
  AKST: 'America/Anchorage', // Alaska Standard Time (UTC-9)
  HST: 'Pacific/Honolulu', // Hawaii Standard Time (UTC-10)
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getIanaTimeZone = (abbr) => tzAbbreviationToIana[abbr] || 'America/New_York';

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getTimeZoneAbbreviation = (timeZone, date = new Date()) => {
  const appTz = appTimezones.find((tz) => tz.iana === timeZone);

  const zonedDate = new TZDate(date, timeZone);
  const utcOffset = dateFnsFormat(zonedDate, " '(UTC'XXX')'", { timeZone });

  return appTz?.code + utcOffset;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getTimeZoneIana = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const formatDayInTimeZone = (date, tzInfo, long = true) => {
  // Use "EEEE MMM d, yyyy" for "Sunday Aug 15, 2025"
  const formatStr = long ? 'EEEE MMM d, yyyy' : 'MM/dd/yy';
  if (!date) return 'N/A';
  if (!tzInfo || tzInfo.local) return dateFnsFormat(date, formatStr);

  const zonedDate = new TZDate(date, tzInfo.iana);
  return dateFnsFormat(zonedDate, formatStr);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const formatDateInTimeZone = (date, tzInfo, sec = false) => {
  const formatStr = sec ? 'h:mm:ss a MM/dd/yy' : 'h:mm a MM/dd/yy';
  if (!date) return 'N/A';
  if (!tzInfo || tzInfo.local) return dateFnsFormat(date, formatStr);

  const zonedDate = new TZDate(date, tzInfo.iana);
  const tzTime = dateFnsFormat(zonedDate, formatStr);
  const appTz = appTimezones.find((tz) => tz.iana === tzInfo.iana);
  if (appTz) return `${tzTime} ${appTz.code}`;
  const abbreviation = new Intl.DateTimeFormat('en-US', { timeZone: tzInfo.iana, timeZoneName: 'short' })
    .formatToParts(zonedDate)
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${tzTime} ${abbreviation}`;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const format24HourDateInTimeZone = (date, tzInfo, sec = false) => {
  const formatStr = sec ? 'HH:mm:ss MM/dd/yy' : 'HH:mm MM/dd/yy';
  if (!date) return 'N/A';
  if (!tzInfo || tzInfo.local) return dateFnsFormat(date, formatStr);

  const zonedDate = new TZDate(date, tzInfo.iana);
  const tzTime = dateFnsFormat(zonedDate, formatStr);
  const appTz = appTimezones.find((tz) => tz.iana === tzInfo.iana);
  if (appTz) return `${tzTime} ${appTz.code}`;
  const abbreviation = new Intl.DateTimeFormat('en-US', { timeZone: tzInfo.iana, timeZoneName: 'short' })
    .formatToParts(zonedDate)
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${tzTime} ${abbreviation}`;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const formatTimeInTimeZone = (date, tzInfo, sec = false) => {
  if (!date) return 'N/A';
  const formatStr = sec ? 'h:mm:ss a' : 'h:mm a';
  if (!tzInfo || tzInfo.local) return dateFnsFormat(date, formatStr);

  const zonedDate = new TZDate(date, tzInfo.iana);
  const tzTime = dateFnsFormat(zonedDate, formatStr);
  const appTz = appTimezones.find((tz) => tz.iana === tzInfo.iana);
  if (appTz) return `${tzTime} ${appTz.code}`;
  const abbreviation = new Intl.DateTimeFormat('en-US', { timeZone: tzInfo.iana, timeZoneName: 'short' })
    .formatToParts(zonedDate)
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${tzTime} ${abbreviation}`;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const convertRangeDatesToIsoStrs = (range) => {
  const isoStrs = {};
  if (range?.from) isoStrs.from = range.from.toISOString();
  if (range?.to) isoStrs.to = range.to.toISOString();
  return isoStrs;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const convertRangeIsoStrsToDates = (isoStrs) => {
  const range = {};
  if (isoStrs.from) range.from = new Date(isoStrs.from);
  if (isoStrs.to) range.to = new Date(isoStrs.to);
  return range;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const createTZDateWithTime = (baseDate, timezoneIana, hours, minutes, seconds, ms) => {
  const date = new TZDate(baseDate, timezoneIana);
  date.setHours(hours, minutes, seconds, ms);
  return date;
};

// ////////////////////////////////////////////////////////////////////////////////////////////////
export const dateRangeToTZDateRange = (dateRange, timezoneIana = 'America/New_York') => {
  return {
    from: dateRange?.from ? new TZDate(dateRange.from, timezoneIana) : undefined,
    to: dateRange?.to ? new TZDate(dateRange.to, timezoneIana) : undefined,
  };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getDateRangeToday = (timezoneIana = 'America/New_York') => {
  const now = new TZDate(new Date(), timezoneIana);
  const from = createTZDateWithTime(now, timezoneIana, 0, 0, 0, 0);
  const to = createTZDateWithTime(now, timezoneIana, 23, 59, 59, 999);
  return { from, to };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getDateRangePast24hrs = (timezoneIana = 'America/New_York') => {
  const range = {
    to: new Date(),
    from: new Date(Date.now() - 1 * 24 * 3600 * 1000),
  };
  return dateRangeToTZDateRange(range, timezoneIana);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getDateRangePast7Days = (timezoneIana = 'America/New_York') => {
  const { from: startOfToday, to: endOfToday } = getDateRangeToday(timezoneIana);
  const pastDate = new TZDate(startOfToday, timezoneIana);
  pastDate.setDate(pastDate.getDate() - 6);
  return { from: pastDate, to: endOfToday };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getDateRangePast30Days = (timezoneIana = 'America/New_York') => {
  const { from: startOfToday, to: endOfToday } = getDateRangeToday(timezoneIana);
  const pastDate = new TZDate(startOfToday, timezoneIana);
  pastDate.setDate(pastDate.getDate() - 29);
  return { from: pastDate, to: endOfToday };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const getDateRangePast60Days = (timezoneIana = 'America/New_York') => {
  const { from: startOfToday, to: endOfToday } = getDateRangeToday(timezoneIana);
  const pastDate = new TZDate(startOfToday, timezoneIana);
  pastDate.setDate(pastDate.getDate() - 59);
  return { from: pastDate, to: endOfToday };
};

// //////////////////////////////////////////////////////////////////////////////////////////////////
export const getNextServiceDate = (selectedDate, daysCount, timezoneIana = 'America/New_York') => {
  const fromDate = new TZDate(selectedDate, timezoneIana);
  const toDate = new TZDate(fromDate, timezoneIana);
  toDate.setDate(toDate.getDate() + parseInt(daysCount));
  return { lastSDate: fromDate, nextSDate: toDate };
};

// //////////////////////////////////////////////////////////////////////////////////////////////////
export const getFirstOfCurrentMonth = (timezoneIana = 'America/New_York') => {
  const now = new TZDate(new Date(), timezoneIana);
  now.setHours(0, 0, 0, 0);
  now.setDate(1);
  return now;
};
